import { itemName, rarityLabel } from '@/i18n';
import { getItem } from '@/domain/items';
import { upgradedStats } from '@/domain/upgrade';
import type { Rarity } from '@/domain/types';
import type { Listing } from '@/state/marketStore';
import { ItemIcon } from './ItemIcon';
import './ListingCard.css';

interface ListingCardProps {
  listing: Listing;
  /** "buy" for marketplace browse, "manage" for my-listings cancel UI */
  variant: 'buy' | 'manage';
  /** Disabled when player cannot afford OR is the seller */
  disabled?: boolean;
  /** Reason for disabled state */
  disabledReason?: string;
  onAction: () => void;
  /** Open detail view (entire card clickable). */
  onOpenDetail?: () => void;
  busy?: boolean;
}

const RARITY_COLOR: Record<Rarity, string> = {
  common: '#a4b8c8',
  uncommon: '#4affb0',
  rare: '#4aa7ff',
  epic: '#c64aff',
  legendary: '#ff9a2b',
  mythic: '#ff4af0',
};

export function ListingCard({ listing, variant, disabled, disabledReason, onAction, onOpenDetail, busy }: ListingCardProps) {
  const def = getItem(listing.itemId);
  if (!def) return null;
  const color = RARITY_COLOR[listing.rarity];
  const stats = def.stats ? upgradedStats(def.stats, listing.upgradeLevel) : null;

  const isOpen = listing.status === 'open';
  const statusLabel =
    listing.status === 'open' ? 'Aktiv' :
    listing.status === 'sold' ? 'Verkauft' :
    listing.status === 'cancelled' ? 'Abgebrochen' :
    'Abgelaufen';

  const clickable = !!onOpenDetail;

  return (
    <div
      className={`listing-card listing-card--${listing.rarity} ${!isOpen ? 'listing-card--closed' : ''} ${clickable ? 'listing-card--clickable' : ''}`}
      style={{ '--rarity-color': color } as React.CSSProperties}
      onClick={clickable ? onOpenDetail : undefined}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onOpenDetail?.();
              }
            }
          : undefined
      }
    >
      <div className="listing-card__icon">
        <ItemIcon itemId={listing.itemId} sizeRem={2.5} />
      </div>
      <div className="listing-card__body">
        <div className="listing-card__name">
          {itemName(def.nameKey)}
          {listing.upgradeLevel > 0 && (
            <span className="listing-card__level"> +{listing.upgradeLevel}</span>
          )}
        </div>
        <div className="listing-card__rarity" style={{ color }}>
          {rarityLabel(listing.rarity)}
          {def.equipSlot && <span className="listing-card__slot"> · {def.equipSlot}</span>}
        </div>
        {stats && (
          <div className="listing-card__stats">
            {stats.attack > 0 && <span>⚔ {stats.attack}</span>}
            {stats.defense > 0 && <span>🛡 {stats.defense}</span>}
            {stats.health > 0 && <span>♥ {stats.health}</span>}
          </div>
        )}
        {variant === 'buy' && (
          <div className="listing-card__seller">
            von <strong>{listing.sellerName}</strong>
          </div>
        )}
        {variant === 'manage' && (
          <div className={`listing-card__status listing-card__status--${listing.status}`}>
            {statusLabel}
          </div>
        )}
      </div>
      <div className="listing-card__action">
        <div className="listing-card__price">◈ {listing.price.toLocaleString('de-DE')}</div>
        {variant === 'buy' && (
          <button
            className="btn btn--primary"
            onClick={(e) => { e.stopPropagation(); onAction(); }}
            disabled={disabled || busy}
            title={disabledReason}
          >
            {busy ? '...' : 'Kaufen'}
          </button>
        )}
        {variant === 'manage' && isOpen && (
          <button
            className="btn btn--ghost"
            onClick={(e) => { e.stopPropagation(); onAction(); }}
            disabled={busy}
          >
            {busy ? '...' : 'Abbrechen'}
          </button>
        )}
      </div>
    </div>
  );
}
